/// <amd-module name="Repos/ChangesCommon/ChangesNavigationService" />
import { GitVersionDescriptor, TfvcVersionDescriptor } from "Repos/Common/Contracts/Git";
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";
import { IChange, IChangeCollection, IChangesNavigationService, IDiffNavigation, IFileNavigation } from "./Contracts";

export abstract class ChangesNavigationService<TVersionDescriptor> extends VssService implements IChangesNavigationService<TVersionDescriptor> {
    private changes: IReadonlyObservableValue<IChangeCollection<TVersionDescriptor> | undefined>;
    private diffNavigation: ObservableValue<IDiffNavigation | undefined>;
    private fileNavigation: ObservableValue<IFileNavigation | undefined>;
    private navigateToFirstDiffOnLoad: boolean;
    private navigateToLastDiffOnLoad: boolean;
    private selectedItem: ObservableValue<IChange>;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.diffNavigation = new ObservableValue<IDiffNavigation | undefined>(undefined);
        this.fileNavigation = new ObservableValue<IFileNavigation | undefined>(undefined);
        this.navigateToFirstDiffOnLoad = false;
        this.navigateToLastDiffOnLoad = false;
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);

        this.removeChangesSubscriber();
    }

    public getDiffNavigation(): IReadonlyObservableValue<IDiffNavigation | undefined> {
        return this.diffNavigation;
    }

    public getFileNavigation(): IReadonlyObservableValue<IFileNavigation | undefined> {
        return this.fileNavigation;
    }

    public getHasNextDiff(): boolean {
        if (
            (this.diffNavigation.value && this.diffNavigation.value.hasNextDiff) ||
            (this.fileNavigation.value && this.fileNavigation.value.hasNextFile)
        ) {
            return true;
        }

        return false;
    }

    public getHasPreviousDiff(): boolean {
        if (
            (this.diffNavigation.value && this.diffNavigation.value.hasPreviousDiff) ||
            (this.fileNavigation.value && this.fileNavigation.value.hasPreviousFile)
        ) {
            return true;
        }
        return false;
    }

    public getNavigateToFirstDiffOnLoad(): boolean {
        return this.navigateToFirstDiffOnLoad;
    }

    public getNavigateToLastDiffOnLoad(): boolean {
        return this.navigateToLastDiffOnLoad;
    }

    public getSelectedItem(): IReadonlyObservableValue<IChange> {
        return this.selectedItem;
    }

    public nextDiff(): void {
        if (this.diffNavigation.value && this.diffNavigation.value.hasNextDiff) {
            this.diffNavigation.value.nextDiff();
        } else if (this.fileNavigation.value && this.fileNavigation.value.hasNextFile) {
            this.fileNavigation.value.nextFile();
        }
    }

    public previousDiff(): void {
        if (this.diffNavigation.value && this.diffNavigation.value.hasPreviousDiff) {
            this.diffNavigation.value.previousDiff();
        } else if (this.fileNavigation.value && this.fileNavigation.value.hasPreviousFile) {
            this.fileNavigation.value.previousFile();
        }
    }

    public setChanges(changes: IReadonlyObservableValue<IChangeCollection<TVersionDescriptor> | undefined>): void {
        this.removeChangesSubscriber();

        this.changes = changes;

        changes.subscribe(this.onUpdatedChanges);
        this.onUpdatedChanges(changes.value);
    }

    public getChanges(): IReadonlyObservableValue<IChangeCollection<TVersionDescriptor> | undefined> {
        return this.changes;
    }

    private onUpdatedChanges = (changesValue: IChangeCollection<TVersionDescriptor> | undefined): void => {
        this.diffNavigation.value = undefined;
        this.fileNavigation.value = undefined;
        this.navigateToFirstDiffOnLoad = false;
        this.navigateToLastDiffOnLoad = false;
        if (changesValue) {
            if (this.selectedItem) {
                this.selectedItem.value = changesValue.rootTreeItem.data;
            } else {
                this.selectedItem = new ObservableValue<IChange>(changesValue.rootTreeItem.data);
                this.selectedItem.subscribe(this.onSelectedItemChanged);
            }
        }
    };

    private removeChangesSubscriber(): void {
        if (this.changes) {
            this.changes.unsubscribe(this.onUpdatedChanges);
        }
    }

    public setDiffNavigation(diffNavigation: IDiffNavigation | undefined): void {
        this.diffNavigation.value = diffNavigation;
    }

    public setSelectedItem(change: IChange, toFirstDiff = false, toLastDiff = false): void {
        this.navigateToFirstDiffOnLoad = toFirstDiff;
        this.navigateToLastDiffOnLoad = toLastDiff;

        if (this.selectedItem.value === change) {
            return;
        }

        this.selectedItem.value = change;
    }

    private onSelectedItemChanged = (change: IChange): void => {
        this.diffNavigation.value = undefined;

        if (!change || change.isFolder) {
            this.fileNavigation.value = undefined;
        } else {
            const nextFile: IChange | undefined = this.findNextFile();
            const previousFile: IChange | undefined = this.findPreviousFile();

            this.fileNavigation.value = {
                hasNextFile: !!nextFile,
                hasPreviousFile: !!previousFile,
                nextFile: () => {
                    if (nextFile) {
                        this.setSelectedItem(nextFile, true, false);
                    }
                },
                previousFile: () => {
                    if (previousFile) {
                        this.setSelectedItem(previousFile, false, true);
                    }
                }
            };
        }
    };

    private findSelectedItemTreePath(treeNode: ITreeItem<IChange>, treePath: ITreeItem<IChange>[], indices: number[]): boolean {
        if (treeNode.data == this.selectedItem.value) {
            return true;
        }

        if (treeNode.childItems) {
            return treeNode.childItems.some((childNode: ITreeItem<IChange>, index: number) => {
                if (this.findSelectedItemTreePath(childNode, treePath, indices)) {
                    treePath.push(treeNode);
                    indices.push(index);
                    return true;
                }
                return false;
            });
        }

        return false;
    }

    private findNextFile(): IChange | undefined {
        if (!this.changes.value) {
            return undefined;
        }

        let treePath: ITreeItem<IChange>[] = [];
        let indices: number[] = [];
        if (this.findSelectedItemTreePath(this.changes.value.rootTreeItem, treePath, indices)) {
            //go up the tree to find the first parent where the last child is not in the path to the selected node
            let treePathIndex: number = 0;
            while (treePath.length > treePathIndex && treePath[treePathIndex].childItems!.length == indices[treePathIndex] + 1) {
                ++treePathIndex;
            }

            if (treePath.length > treePathIndex) {
                //go to the next sibling
                let nextNode: ITreeItem<IChange> = treePath[treePathIndex].childItems![indices[treePathIndex] + 1];

                //and find the first leaf file child (not a discussion)
                while (nextNode.data.isFolder && nextNode.childItems) {
                    nextNode = nextNode.childItems[0];
                }

                return nextNode.data;
            }
        }

        return undefined;
    }

    private findPreviousFile(): IChange | undefined {
        if (!this.changes.value) {
            return undefined;
        }

        let treePath: ITreeItem<IChange>[] = [];
        let indices: number[] = [];
        if (this.findSelectedItemTreePath(this.changes.value.rootTreeItem, treePath, indices)) {
            //go up the tree to find the first parent where the first child is not in the path to the selected node
            let treePathIndex: number = 0;
            while (treePath.length > treePathIndex && indices[treePathIndex] == 0) {
                ++treePathIndex;
            }

            if (treePath.length > treePathIndex) {
                //go to the previous sibling
                let previousNode: ITreeItem<IChange> = treePath[treePathIndex].childItems![indices[treePathIndex] - 1];

                //and find the last leaf file child (not a discussion)
                while (previousNode.data.isFolder && previousNode.childItems) {
                    previousNode = previousNode.childItems[previousNode.childItems.length - 1];
                }

                return previousNode.data;
            }
        }

        return undefined;
    }
}

const gitServiceName = "IGitNavigationService";

export interface IGitNavigationService extends IVssService, IChangesNavigationService<GitVersionDescriptor> {}
class GitNavigationService extends ChangesNavigationService<GitVersionDescriptor> implements IGitNavigationService {}
Services.add(gitServiceName, { serviceFactory: GitNavigationService });

export function getGitNavigationService(pageContext: IVssPageContext): IGitNavigationService {
    return pageContext.getService<IGitNavigationService>(gitServiceName);
}

const tfvcServiceName = "ITfvcNavigationService";

export interface ITfvcNavigationService extends IVssService, IChangesNavigationService<TfvcVersionDescriptor> {}
class TfvcNavigationService extends ChangesNavigationService<TfvcVersionDescriptor> implements ITfvcNavigationService {}
Services.add(tfvcServiceName, { serviceFactory: TfvcNavigationService });

export function getTfvcNavigationService(pageContext: IVssPageContext): ITfvcNavigationService {
    return pageContext.getService<ITfvcNavigationService>(tfvcServiceName);
}
